import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/authStore'

// 🔥 修复：创建消息服务，避免静态函数警告
class MessageService {
  error(content: string) {
    // 延迟执行，确保在组件上下文中调用
    setTimeout(() => {
      try {
        const { message } = require('antd')
        message.error(content)
      } catch (e) {
        console.error('Message error:', content)
      }
    }, 0)
  }
}

const messageService = new MessageService()

// 创建axios实例
const createApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: '/api',
    timeout: 30000,  // 🔥 修复：增加超时时间到30秒，解决数据库查询超时问题
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // 请求拦截器 - 添加认证头
  instance.interceptors.request.use(
    (config) => {
      const token = useAuthStore.getState().token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // 响应拦截器 - 处理错误和认证
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response
    },
    (error) => {
      const { response } = error

      if (response?.status === 401) {
        // Token过期或无效，清除认证状态
        useAuthStore.getState().logout()
        messageService.error('登录已过期，请重新登录')
        window.location.href = '/login'
        return Promise.reject(error)
      }

      if (response?.status === 403) {
        messageService.error('权限不足')
        return Promise.reject(error)
      }

      if (response?.status >= 500) {
        messageService.error('服务器错误，请稍后重试')
        return Promise.reject(error)
      }

      if (response?.data?.message) {
        messageService.error(response.data.message)
      } else if (error.message) {
        messageService.error(error.message)
      }

      return Promise.reject(error)
    }
  )

  return instance
}

// API实例
const api = createApiInstance()

// 通用API响应类型
export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
  error?: string
  pagination?: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// 通用请求方法
class ApiService {
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response = await api.request<T>(config)
    return response.data
  }

  async get<T>(url: string, params?: any): Promise<T> {
    return this.request<T>({ method: 'GET', url, params })
  }

  async post<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'POST', url, data })
  }

  async put<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'PUT', url, data })
  }

  async delete<T>(url: string): Promise<T> {
    return this.request<T>({ method: 'DELETE', url })
  }
}

export const apiService = new ApiService()

// 认证相关API
export const authApi = {
  login: (username: string, password: string) =>
    apiService.post<ApiResponse<{ token: string; user: any }>>('/auth/login', {
      username,
      password,
    }),

  logout: () => apiService.post<ApiResponse>('/auth/logout'),

  refreshToken: () => apiService.post<ApiResponse<{ token: string; user: any }>>('/auth/refresh'),

  getUserInfo: () => apiService.get<ApiResponse<any>>('/auth/me'),

  verifyToken: () => apiService.get<ApiResponse<{ user: any }>>('/auth/verify'),

  updateProfile: (data: { username?: string; email?: string }) =>
    apiService.put<ApiResponse>('/auth/profile', data),

  changePassword: (data: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    apiService.put<ApiResponse>('/auth/change-password', data),
}

// 会话相关API
export const conversationApi = {
  getConversations: (params: {
    page?: number
    limit?: number
    userId?: string
    model?: string
    keyword?: string
    startDate?: number
    endDate?: number
  }) => apiService.get<ApiResponse<any[]>>('/conversations', params),

  // 🔥 新增：获取增强的聊天分组数据
  getChatGroups: (params: {
    userId?: string
    model?: string
    keyword?: string
    dateRange?: [number, number]
    contextMode?: 'all' | 'canvas-based' | 'chat-based'
    clientType?: string
    canvasType?: 'all' | 'real' | 'virtual'
    hasImages?: boolean
    minTokens?: number
    maxTokens?: number
    minCost?: number
    maxCost?: number
    isArchived?: boolean
  }) => apiService.get<ApiResponse<any[]>>('/conversations/groups/enhanced', params),

  // 🔥 新增：批量操作
  batchOperations: (action: string, conversationIds: number[], params?: any) =>
    apiService.post<ApiResponse<any>>('/conversations/batch-operations', {
      action,
      conversationIds,
      params
    }),

  // 🔥 新增：获取客户端使用统计
  getClientUsageStats: (params?: { dateRange?: [number, number] }) =>
    apiService.get<ApiResponse<any[]>>('/conversations/client-stats', params),

  getConversationDetail: (userId: string, chatId: string) =>
    apiService.get<ApiResponse<any>>(`/conversations/${userId}/${chatId}`),

  getStats: (params?: { startDate?: number; endDate?: number }) =>
    apiService.get<ApiResponse<any>>('/conversations/stats/overview', params),

  exportConversations: async (params: any) => {
    try {
      const token = useAuthStore.getState().token
      if (!token) {
        throw new Error('未登录，无法导出数据')
      }

      // 🔥 修复：过滤undefined参数，避免NaN错误
      const filteredParams: any = {}
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          filteredParams[key] = params[key]
        }
      })

      const queryString = new URLSearchParams(filteredParams).toString()
      const response = await fetch(`/api/conversations/export/csv?${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}`)
      }

      // 获取文件名
      const contentDisposition = response.headers.get('Content-Disposition')
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `conversations-${Date.now()}.csv`

      // 下载文件
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      return { success: true, message: '导出成功' }
    } catch (error: any) {
      console.error('导出失败:', error)
      throw error
    }
  },

  // 🔥 新增：删除会话
  deleteConversation: (userId: string, chatId: string) =>
    apiService.delete<ApiResponse<any>>(`/conversations/${userId}/${chatId}`),

  // 🔥 新增：批量删除会话
  deleteConversations: (conversationIds: number[]) =>
    apiService.request<ApiResponse<any>>({
      method: 'DELETE',
      url: '/conversations/batch',
      data: { conversationIds }
    }),

  // 🔥 新增：删除单条消息
  deleteMessage: (messageId: number) =>
    apiService.delete<ApiResponse<any>>(`/conversations/messages/${messageId}`),
}

// 计费相关API
export const billingApi = {
  getBillingRecords: (params: any) =>
    apiService.get<ApiResponse<any[]>>('/billing/records', params),

  getBillingStats: (params: any) =>
    apiService.get<ApiResponse<any>>('/billing/stats', params),

  getConversationBilling: (conversationId: string) =>
    apiService.get<ApiResponse<any>>(`/billing/conversations/${conversationId}`),

  exportBillingRecords: async (params: any) => {
    try {
      const token = useAuthStore.getState().token
      if (!token) {
        throw new Error('未登录，无法导出数据')
      }

      const queryString = new URLSearchParams(params).toString()
      const response = await fetch(`/api/billing/records/export/csv?${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}`)
      }

      // 获取文件名
      const contentDisposition = response.headers.get('Content-Disposition')
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `billing-records-${Date.now()}.csv`

      // 下载文件
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      return { success: true, message: '导出成功' }
    } catch (error: any) {
      console.error('导出失败:', error)
      throw error
    }
  },

  batchProcessHistorical: (limit: number) =>
    apiService.post<ApiResponse<any>>('/billing/batch/historical', { limit }),

  getModelPricing: () =>
    apiService.get<ApiResponse<any[]>>('/billing/pricing'),

  updateModelPricing: (modelId: string, data: any) =>
    apiService.put<ApiResponse<any>>(`/billing/pricing/${modelId}`, data),

  addModelPricing: (data: any) =>
    apiService.post<ApiResponse<any>>('/billing/pricing', data),

  deleteModelPricing: (modelId: string) =>
    apiService.delete<ApiResponse<any>>(`/billing/pricing/${modelId}`),

  // 🔥 新增：批量生成缺失的计费记录
  generateMissingBillingRecords: () =>
    apiService.post<ApiResponse<any>>('/billing/batch/generate-missing', {}),

  // 🔥 新增：重新计算billing_records费用
  recalculateBillingRecordsCosts: () =>
    apiService.post<ApiResponse<any>>('/billing/batch/recalculate-costs', {}),
}

// 分析相关API
export const analyticsApi = {
  getModelUsageStats: (params?: any) =>
    apiService.get<ApiResponse<any>>('/analytics/models/usage', params),

  getTokenUsageTrends: (params?: any) =>
    apiService.get<ApiResponse<any>>('/analytics/tokens/trends', params),

  getCostAnalysis: (params?: any) =>
    apiService.get<ApiResponse<any>>('/analytics/costs/analysis', params),

  getUserUsageAnalysis: (params?: any) =>
    apiService.get<ApiResponse<any>>('/analytics/users/usage', params),

  // 🔥 新增：用户行为分析
  getUserBehaviorAnalysis: (params?: any) =>
    apiService.get<ApiResponse<any>>('/analytics/users/behavior', params),

  getDashboardData: (params?: any) =>
    apiService.get<ApiResponse<any>>('/analytics/dashboard', params),

  getRealTimeStats: () =>
    apiService.get<ApiResponse<any>>('/analytics/realtime'),

  getComparisonAnalysis: (params: any) =>
    apiService.get<ApiResponse<any>>('/analytics/comparison', params),

  exportAnalyticsReport: async (params: any) => {
    try {
      const token = useAuthStore.getState().token
      if (!token) {
        throw new Error('未登录，无法导出数据')
      }

      // 🔥 修复：过滤undefined参数，避免NaN错误
      const filteredParams: any = {}
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          filteredParams[key] = params[key]
        }
      })

      const queryString = new URLSearchParams(filteredParams).toString()
      const response = await fetch(`/api/analytics/export?${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}`)
      }

      // 获取文件名
      const contentDisposition = response.headers.get('Content-Disposition')
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `analytics-report-${Date.now()}.csv`

      // 下载文件
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      return { success: true, message: '导出成功' }
    } catch (error: any) {
      console.error('导出失败:', error)
      throw error
    }
  },
}

// 监控相关API
export const monitoringApi = {
  getMonitoringOverview: () =>
    apiService.get<ApiResponse<any>>('/monitoring/overview'),

  getProxyStatus: () =>
    apiService.get<ApiResponse<any>>('/monitoring/proxy/status'),

  getProxyInfo: () =>
    apiService.get<ApiResponse<any>>('/monitoring/proxy/info'),

  getProxyStats: () =>
    apiService.get<ApiResponse<any>>('/monitoring/proxy/stats'),

  testProxyConnection: () =>
    apiService.post<ApiResponse<any>>('/monitoring/proxy/test'),

  getApiLogs: (params?: any) =>
    apiService.get<ApiResponse<any>>('/monitoring/logs/api', params),

  getErrorLogs: (params?: any) =>
    apiService.get<ApiResponse<any>>('/monitoring/logs/errors', params),

  clearLogs: (data: any) =>
    apiService.post<ApiResponse<any>>('/monitoring/logs/clear', data),

  syncProxyData: () =>
    apiService.post<ApiResponse<any>>('/monitoring/sync'),

  getErrorStats: () =>
    apiService.get<ApiResponse<any>>('/monitoring/stats/errors'),

  getPerformanceStats: () =>
    apiService.get<ApiResponse<any>>('/monitoring/stats/performance'),

  getAuthStatus: () =>
    apiService.get<ApiResponse<any>>('/monitoring/auth/status'),

  getPerformanceMetrics: () =>
    apiService.get<ApiResponse<any>>('/monitoring/metrics'),

  restartProxy: () =>
    apiService.post<ApiResponse<any>>('/monitoring/proxy/restart'),

  updateProxyConfig: (config: any) =>
    apiService.put<ApiResponse<any>>('/monitoring/proxy/config', config),

  exportMonitoringLogs: (params: any) =>
    apiService.get<any>('/monitoring/logs/export', params),
}

// 系统相关API
export const systemApi = {
  getSystemInfo: () => apiService.get<ApiResponse<any>>('/system/info'),

  getHealth: () => apiService.get<ApiResponse<any>>('/health'),
}

// 模型配置管理API
export const modelConfigApi = {
  // 获取所有模型配置
  getAllModels: (params?: { status?: string; provider?: string; search?: string }) =>
    apiService.get<ApiResponse<any[]>>('/models', params),

  // 获取单个模型配置
  getModelById: (id: string) =>
    apiService.get<ApiResponse<any>>(`/models/${id}`),

  // 创建模型配置
  createModel: (data: any) =>
    apiService.post<ApiResponse<any>>('/models', data),

  // 更新模型配置
  updateModel: (id: string, data: any) =>
    apiService.put<ApiResponse<any>>(`/models/${id}`, data),

  // 删除模型配置
  deleteModel: (id: string) =>
    apiService.delete<ApiResponse<any>>(`/models/${id}`),

  // 批量更新价格
  batchUpdatePrices: (updates: any[]) =>
    apiService.post<ApiResponse<any>>('/models/batch-update-prices', { updates }),

  // 获取提供商列表
  getProviders: () =>
    apiService.get<ApiResponse<string[]>>('/models/meta/providers'),

  // 从proxy.js同步模型配置
  syncFromProxy: () =>
    apiService.post<ApiResponse<any>>('/models/sync-from-proxy'),

  // 🔥 新增：获取完整的模型配置列表（联合查询pricing和configs）
  getModelConfigsWithDetails: (params?: {
    provider?: string;
    model_type?: string;
    tier?: string;
    is_active?: boolean;
    supports_vision?: boolean;
    supports_audio?: boolean;
    supports_code?: boolean;
    supports_reasoning?: boolean;
    page?: number;
    limit?: number;
  }) =>
    apiService.get<ApiResponse<any>>('/models/configs-with-details', params),
}

// 🔥 新增：代理设置API
export const proxySettingsApi = {
  // 获取所有设置
  getAllSettings: (params?: {
    category?: string
    isActive?: boolean
  }) => apiService.get<ApiResponse<any>>('/proxy-settings', params),

  // 根据键名获取设置
  getSettingByKey: (settingKey: string) =>
    apiService.get<ApiResponse<any>>(`/proxy-settings/${settingKey}`),

  // 更新设置
  updateSetting: (settingKey: string, settingValue: any) =>
    apiService.put<ApiResponse<any>>(`/proxy-settings/${settingKey}`, { settingValue }),

  // 批量更新设置
  batchUpdateSettings: (settings: Array<{ settingKey: string; settingValue: any }>) =>
    apiService.put<ApiResponse<any>>('/proxy-settings/batch', { settings }),

  // 创建新设置
  createSetting: (settingData: {
    settingKey: string
    settingValue: any
    settingType?: string
    category?: string
    description?: string
    isActive?: boolean
  }) => apiService.post<ApiResponse<any>>('/proxy-settings', settingData),

  // 删除设置
  deleteSetting: (settingKey: string) =>
    apiService.delete<ApiResponse<any>>(`/proxy-settings/${settingKey}`),

  // 获取分类列表
  getCategories: () =>
    apiService.get<ApiResponse<any>>('/proxy-settings/categories'),

  // 获取搜索功能配置
  getSearchConfig: () =>
    apiService.get<ApiResponse<any>>('/proxy-settings/search/config'),

  // 重置设置为默认值
  resetToDefaults: (params?: { category?: string }) =>
    apiService.post<ApiResponse<any>>('/proxy-settings/reset', params),

  // 推送配置到代理服务
  pushToProxy: () =>
    apiService.post<ApiResponse<any>>('/proxy-settings/push-to-proxy')
}

export default api
